import { useState, useRef, useEffect } from 'react';
import { PaperAirplaneIcon, PhotoIcon } from './icons';
import { useClipStore } from '../stores/clipStore';
import { useWebSocket } from '../hooks/useWebSocket';
import { useI18n } from '../i18n';

export default function InputBar() {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const connected = useClipStore((state) => state.connected);
  const { sendMessage, sendImage } = useWebSocket();
  const { t } = useI18n();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim() || !connected || sending) {
      return;
    }

    setSending(true);
    const success = await sendMessage(message.trim());
    if (success) {
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
    // Shift+Enter will insert a newline (default behavior)
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  // Handle paste for images
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            await handleImageSend(file);
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [connected]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[InputBar] Image select triggered');
    const file = e.target.files?.[0];
    console.log('[InputBar] Selected file:', file?.name, 'type:', file?.type, 'size:', file?.size);
    if (file) {
      await handleImageSend(file);
    } else {
      console.log('[InputBar] No file selected');
    }
    e.target.value = '';
  };

  const handleImageSend = async (file: File) => {
    console.log('[InputBar] handleImageSend called, connected:', connected, 'sending:', sending);
    if (!connected || sending) {
      console.log('[InputBar] Cannot send - connected:', connected, 'sending:', sending);
      return;
    }

    // Max 100MB for chunked transfer
    const MAX_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      console.log('[InputBar] File too large:', file.size);
      alert(t.inputBar.fileTooLarge || 'File too large (max 100MB)');
      return;
    }

    setSending(true);
    setUploadProgress(0);

    try {
      console.log('[InputBar] Calling sendImage...');
      // sendImage now handles both small (<1MB) and large images automatically
      const success = await sendImage(file, (progress) => {
        setUploadProgress(progress);
      });
      console.log('[InputBar] sendImage result:', success);
      if (!success) {
        alert(t.inputBar.failedToSend);
      }
    } catch (e) {
      console.error('[InputBar] Error in handleImageSend:', e);
      alert('Error: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="border-t border-gray-200 bg-white px-4 py-3">
      <form onSubmit={handleSubmit} className="flex items-center gap-2 max-w-4xl mx-auto">
        {/* Image Upload Button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!connected || sending}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          title={t.inputBar.uploadImage}
        >
          <PhotoIcon className="w-5 h-5" />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleImageSelect}
          className="hidden"
        />

        {/* Text Input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={connected ? t.inputBar.placeholder : t.inputBar.connecting}
            disabled={!connected || sending}
            rows={1}
            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 resize-none overflow-hidden"
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
        </div>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!message.trim() || !connected || sending}
          className="p-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl transition-colors"
          title={t.inputBar.send}
        >
          {sending ? (
            uploadProgress > 0 ? (
              <span className="text-xs font-medium">{uploadProgress}%</span>
            ) : (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )
          ) : (
            <PaperAirplaneIcon className="w-5 h-5" />
          )}
        </button>
      </form>
    </div>
  );
}
